import modelMetadata from "../data/model_metadata.json";
import icarusCSV from "../data/results_icarus_november_2025.csv" with { type: "text" };
import verilatorCSV from "../data/results_verilator_november_2025.csv" with { type: "text" };
import notSoTinyCSV from "../data/results_NotSoTiny-25-12.csv" with { type: "text" };
import ruCCSV from "../data/results_RuC_NST_32k.csv" with { type: "text" };


export interface ModelMetadata {
  url: string;
  params: number | null;
  category: "General" | "Coding" | "RTL-Specific";
  version: string;
  architecture: "Dense" | "Reasoning";
  context_length: string | null;
  deprecated?: boolean;
}

export interface LeaderboardRow {
  rank: number;
  modelName: string;
  modelType: string;
  params: number | null;
  contextLength: string | null;
  url: string;
  version: string;
  architecture: string;
  promptMode?: string;
  [key: string]: any; // For dynamic metric columns
}

function getModelMetadata(modelName: string): ModelMetadata | null {
  const metadata = modelMetadata as Record<string, ModelMetadata>;

  const trimmedName = modelName.trim();
  if (metadata[trimmedName]) {
    return metadata[trimmedName];
  }

  if (metadata[modelName]) {
    return metadata[modelName];
  }

  return null;
}

function parseCSV(text: string): string[][] {
  // Remove BOM if present
  const cleanText = text.replace(/^\uFEFF/, '');
  const lines = cleanText.split("\n").filter(line => line.trim());
  return lines.map(line => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });
}

function getCSVText(task: string, simulator: string | null): string {
  if (task === "Module Completion") {
    return notSoTinyCSV;
  } else if (task === "Rule Completion") {
    return ruCCSV;
  } else if (task === "Line Completion") {
    return icarusCSV;
  } else if (simulator === "Icarus Verilog") {
    return icarusCSV;
  } else if (simulator === "Verilator") {
    return verilatorCSV;
  }
  return "";
}

function findBenchmarkColumns(
  metricRow: string[],
  benchmarkRow: string[],
  benchmark: string,
  task: string
): { indices: number[]; metrics: string[] } {
  const indices: number[] = [];
  const metrics: string[] = [];

  if (benchmark === "All") {
    const taskBenchmarks = getTaskBenchmarks(task);
    for (const bench of taskBenchmarks) {
      for (let i = 0; i < benchmarkRow.length; i++) {
        if (benchmarkRow[i] === bench && !indices.includes(i)) {
          indices.push(i);
          metrics.push(metricRow[i]);
        }
      }
    }
  } else if (benchmark === "NotSoTiny") {
    for (let i = 1; i < metricRow.length; i++) {
      if (metricRow[i]) {
        indices.push(i);
        metrics.push(metricRow[i]);
      }
    }
  } else {
    for (let i = 0; i < benchmarkRow.length; i++) {
      if (benchmarkRow[i] === benchmark || benchmarkRow[i].includes(benchmark)) {
        indices.push(i);
        metrics.push(metricRow[i]);
      }
    }
  }

  return { indices, metrics };
}

function getTaskBenchmarks(task: string): string[] {
  const benchmarks: Record<string, string[]> = {
    "Spec-to-RTL": ["VerilogEval S2R", "RTLLM"],
    "Code Completion": ["VerilogEval MC", "VeriGen"],
    "Module Completion": ["TT06", "TT07", "TT08", "TT09", "TT10 IHP 02", "TT10 IHP 25a", "TTSky25a"],
    "Rule Completion": ["RuC"],
    "Line Completion": ["RTL-Repo"]
  };
  return benchmarks[task] || [];
}

export async function loadLeaderboardData(
  task: string,
  benchmark: string,
  simulator: string | null,
  filters: {
    modelTypes: string[];
    maxParams: number;
    includeDeprecated: boolean;
    searchQuery: string;
  }
): Promise<LeaderboardRow[]> {
  const csvText = getCSVText(task, simulator);
  if (!csvText) {
    console.error("Invalid task/simulator combination");
    return [];
  }

  try {
    const csvData = parseCSV(csvText);

    if (csvData.length < 3) {
      console.error("Invalid CSV format");
      return [];
    }

    const metricRow = task === "Module Completion" ? csvData[1] : csvData[0];
    const benchmarkRow = task === "Module Completion" ? csvData[0] : csvData[1];
    const dataRows = csvData.slice(2);

    const { indices, metrics } = findBenchmarkColumns(metricRow, benchmarkRow, benchmark, task);

    const rows: LeaderboardRow[] = [];

    for (const row of dataRows) {
      const modelName = row[0];
      if (!modelName) continue;

      const metadata = getModelMetadata(modelName);

      // Fallback for missing metadata
      const modelType = metadata?.category || "*";
      const params = metadata?.params || null;
      const contextLength = metadata?.context_length || "*";
      const url = metadata?.url || "*";
      const version = metadata?.version || "*";
      const architecture = metadata?.architecture || "*";

      // Filter deprecated models
      if (metadata?.deprecated && !filters.includeDeprecated) {
        continue;
      }

      if (filters.modelTypes.length > 0) {
        const hasReasoningFilter = filters.modelTypes.includes("Reasoning");
        const categoryFilters = filters.modelTypes.filter(t => t !== "Reasoning");
        const hasCategoryFilter = categoryFilters.length > 0;

        if (hasReasoningFilter && hasCategoryFilter) {
          // Both category and reasoning selected - require both (AND)
          const matchesCategory = categoryFilters.includes(modelType);
          const matchesArchitecture = architecture === "Reasoning";
          if (!matchesCategory || !matchesArchitecture) {
            continue;
          }
        } else if (hasReasoningFilter) {
          // Only reasoning selected
          if (architecture !== "Reasoning") {
            continue;
          }
        } else if (hasCategoryFilter) {
          // Only category selected
          if (!categoryFilters.includes(modelType)) {
            continue;
          }
        }
      }

      if (params && params > filters.maxParams) {
        continue;
      }

      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        if (!modelName.toLowerCase().includes(query)) {
          continue;
        }
      }

      const rowData: LeaderboardRow = {
        rank: 0,
        modelName,
        modelType,
        params,
        contextLength,
        url,
        version,
        architecture,
      };

      const benchmarkMetrics: Record<string, Record<string, number>> = {};

      for (let i = 0; i < indices.length; i++) {
        const metricName = metrics[i];
        const colIndex = indices[i];
        const benchmarkName = benchmarkRow[colIndex];
        const value = row[colIndex];

        if (!benchmarkMetrics[benchmarkName]) {
          benchmarkMetrics[benchmarkName] = {};
        }
        benchmarkMetrics[benchmarkName][metricName] = value ? parseFloat(value) : 0;
      }

      if (benchmark === "All" && (task === "Spec-to-RTL" || task === "Code Completion")) {
        let bench1, bench2, w1, w2;

        if (task === "Spec-to-RTL") {
          bench1 = benchmarkMetrics["VerilogEval S2R"];
          bench2 = benchmarkMetrics["RTLLM"];
          w1 = 155;
          w2 = 47;
        } else { // Code Completion
          bench1 = benchmarkMetrics["VerilogEval MC"];
          bench2 = benchmarkMetrics["VeriGen"];
          w1 = 155;
          w2 = 17;
        }

        if (bench1 && bench2) {
          const metricsToAggregate = ['Syntax (STX)', 'Functionality (FNC)', 'Synthesis (SYN)', 'Power', 'Performance', 'Area'];

          for (const metric of metricsToAggregate) {
            if (bench1[metric] !== undefined && bench2[metric] !== undefined) {
              const aggValue = (w1 * bench1[metric] + w2 * bench2[metric]) / (w1 + w2);
              rowData[metric] = aggValue;
            }
          }

          if (bench1.Power && bench1.Performance && bench1.Area &&
              bench2.Power && bench2.Performance && bench2.Area) {
            const ppa1 = computePPAScore(bench1.Power, bench1.Performance, bench1.Area);
            const ppa2 = computePPAScore(bench2.Power, bench2.Performance, bench2.Area);
            const aggPPA = (w1 * ppa1 + w2 * ppa2) / (w1 + w2);
            rowData["Aggregated Score"] = aggPPA;
          }
        }
      } else if (benchmark === "NotSoTiny") {
        const metricGroups: Record<string, number[]> = {};

        for (let i = 0; i < indices.length; i++) {
          let metricName = metrics[i];
          const colIndex = indices[i];
          const value = row[colIndex];
          const numValue = value ? parseFloat(value) : 0;

          if (metricName === "STX") metricName = "Syntax (STX)";
          else if (metricName === "EQV") metricName = "Functionality (EQV)";

          if (!metricGroups[metricName]) {
            metricGroups[metricName] = [];
          }
          metricGroups[metricName].push(numValue);
        }

        for (const metricName in metricGroups) {
          const values = metricGroups[metricName];
          const mean = values.reduce((a, b) => a + b, 0) / values.length;
          rowData[metricName] = mean;
        }
      } else if (task === "Rule Completion") {
        // RuC specific logic: 
        // row[0] = Model, row[1] = Prompt Mode
        rowData.promptMode = row[1];
        
        // Get AVG (STX) and AVG (EQV)
        // In the CSV: AVG (STX) is at index 11, AVG (EQV) is at index 21
        const stxVal = row[11] ? parseFloat(row[11].replace('%', '')) : 0;
        const eqvVal = row[21] ? parseFloat(row[21].replace('%', '')) : 0;
        
        rowData["Syntax"] = stxVal;
        rowData["Functionality"] = eqvVal;

        // Store detailed scores
        // STX: index 2 to 10
        const stxRules = ["INST", "CONT", "BLK", "NBLK", "CASE", "COND", "ALWS", "PARAM", "PORT"];
        stxRules.forEach((rule, i) => {
          rowData[`${rule} (STX)`] = row[2 + i] ? parseFloat(row[2 + i].replace('%', '')) : 0;
        });

        // EQV: index 12 to 20
        stxRules.forEach((rule, i) => {
          rowData[`${rule} (EQV)`] = row[12 + i] ? parseFloat(row[12 + i].replace('%', '')) : 0;
        });
      } else {
        for (let i = 0; i < indices.length; i++) {
          const metricName = metrics[i];
          const colIndex = indices[i];
          const value = row[colIndex];
          rowData[metricName] = value ? parseFloat(value) : 0;
        }

        const power = rowData["Power"];
        const performance = rowData["Performance"];
        const area = rowData["Area"];
        if (power !== undefined && performance !== undefined && area !== undefined) {
          rowData["Aggregated Score"] = computePPAScore(power, performance, area);
        }
      }

      if (task === "Line Completion") {
        const hasInvalidValue = Object.values(rowData).some(val => val === -1);
        if (hasInvalidValue) {
          continue;
        }
      }

      rows.push(rowData);
    }

    const primaryMetric = getPrimaryMetric(task, benchmark, simulator, metrics);
    rows.sort((a, b) => {
      const aValue = typeof a[primaryMetric] === 'number' ? a[primaryMetric] as number : 0;
      const bValue = typeof b[primaryMetric] === 'number' ? b[primaryMetric] as number : 0;
      return bValue - aValue;
    });

    rows.forEach((row, index) => {
      row.rank = index + 1;
    });

    return rows;
  } catch (error) {
    console.error("Error loading leaderboard data:", error);
    return [];
  }
}

// Compute PPA-Score (average of Power, Performance, Area)
function computePPAScore(power: number, performance: number, area: number): number {
  return (power + performance + area) / 3;
}

// Aggregate S2R metrics with weighted average
function aggregateS2RMetrics(verilogEvalPPA: number | null, rtllmPPA: number | null): number | null {
  if (verilogEvalPPA === null || rtllmPPA === null) {
    return null;
  }
  const w1 = 155; // VerilogEval S2R samples
  const w2 = 47;  // RTLLM samples
  return (w1 * verilogEvalPPA + w2 * rtllmPPA) / (w1 + w2);
}

// Aggregate MC metrics with weighted average
function aggregateMCMetrics(verilogEvalPPA: number | null, verigenPPA: number | null): number | null {
  if (verilogEvalPPA === null || verigenPPA === null) {
    return null;
  }
  const w1 = 155; // VerilogEval MC samples
  const w2 = 17;  // VeriGen samples
  return (w1 * verilogEvalPPA + w2 * verigenPPA) / (w1 + w2);
}

function getPrimaryMetric(task: string, benchmark: string, simulator: string | null, metrics: string[]): string {
  // For "All" benchmark with S2R or MC, use the aggregated PPA score
  if (benchmark === "All" && (task === "Spec-to-RTL" || task === "Code Completion")) {
    return "Aggregated Score";
  }

  // For Rule Completion, sort by Functionality
  if (task === "Rule Completion") {
    return "Functionality";
  }

  // For single benchmarks with Spec-to-RTL or Code Completion, sort by Functionality
  if (task === "Spec-to-RTL" || task === "Code Completion") {
    const fncMetric = metrics.find(m => m.includes("Functionality") || m.includes("FNC"));
    if (fncMetric) {
      return fncMetric;
    }
  }

  // For Module Completion with YoSys EQV, sort by Functionality (EQV)
  if (task === "Module Completion") {
    const eqvMetric = metrics.find(m => m.includes("EQV") || m.includes("Functionality"));
    if (eqvMetric) {
      return eqvMetric;
    }
  }

  // For Line Completion, sort by EM (Exact Match)
  if (task === "Line Completion") {
    return "EM";
  }

  // Fallback: use the last metric (usually the aggregate score)
  if (metrics.length > 0) {
    return metrics[metrics.length - 1];
  }

  return metrics[0] || "";
}
